import { SANITY_PROJECT_ID } from "@/common/constants";
import { DynamicTranslationKey } from "@/models/types";
import {
  createImageUrlBuilder,
  type SanityImageSource,
} from "@sanity/image-url";
import { placeholderImageUrl } from "./config";
import { useDynamicTranslationsContext } from "./dynamicTranslationsContext";

export const imageSizes = {
  icon: { width: 288, quality: 90 },
  logo: { width: 400, quality: 90 },
  thumbnail: { width: 800, quality: 90 },
  hero: { width: 1600, quality: 90 },
  full: { width: 1920, quality: 90 },
  wide: { width: 2560, quality: 90 },
} as const;

export type ImageSize = keyof typeof imageSizes;

/**
 * Generate a UUID, with fallback for non-secure contexts (HTTP)
 * crypto.randomUUID() is only available in secure contexts (HTTPS/localhost)
 */
export function generateUUID(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  console.warn(
    "[generateUUID] crypto.randomUUID not available (non-secure context), using fallback"
  );

  // Fallback using crypto.getRandomValues if available
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r =
        (crypto.getRandomValues(new Uint8Array(1))[0] & 15) >>
        (c === "x" ? 0 : 3);
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  console.warn(
    "[generateUUID] crypto.getRandomValues not available, using Math.random fallback"
  );

  // Final fallback using Math.random (less secure but works everywhere)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getImageUrl(
  image: SanityImageSource | null | undefined,
  size: ImageSize,
  context?: { docTitle?: string; docSlug?: string }
): string {
  if (!image) {
    console.error(
      `[getImageUrl] Missing image for document: ${context?.docTitle || context?.docSlug || "unknown"}`
    );
    return placeholderImageUrl;
  }

  const { width, quality } = imageSizes[size];
  const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET || "production";
  const builder = createImageUrlBuilder({
    projectId: SANITY_PROJECT_ID,
    dataset,
  });

  try {
    return builder
      .image(image)
      .width(width)
      .fit("max")
      .quality(quality)
      .auto("format")
      .url();
  } catch (error) {
    console.error(
      `[getImageUrl] Failed to build URL for document: ${context?.docTitle || context?.docSlug || "unknown"}`,
      error
    );
    return placeholderImageUrl;
  }
}

export function toTelHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

export function toMailtoHref(email: string): string {
  const match = email.match(/[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/);
  return `mailto:${match?.[0] ?? email.trim()}`;
}

export const useDynamicTranslations = () => {
  const { translations } = useDynamicTranslationsContext();
  return (key: DynamicTranslationKey): string => {
    const text = translations[key] ?? key;
    return text.replaceAll(`\\n`, `\n`);
  };
};
